import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";

// Import components
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { SilaeTabSkeleton } from "@/components/silae/SilaeTabSkeleton";

// Import services
import authService from "@/services/authService";

// Types
type UserData = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  userType: "individual" | "company";
  companyName: string | null;
  siret: string | null;
};

// Context for sharing dashboard data
export const DashboardContext = React.createContext<{
  user: UserData | null;
  loading: boolean;
  error: string | null;
} | null>(null);

export const DashboardLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<UserData | null>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname;

    if (path.includes("/subscription")) return "subscription";
    if (path.includes("/code-generator")) return "code-generator";
    if (path.includes("/variable_library")) return "variable_library";
    if (path.includes("/code-editor")) return "code-editor";
    if (path.includes("/ai-generator")) return "ai-generator";
    if (path.includes("/profile")) return "profile";

    return "code-generator"; // Default to silae instead of profile
  };

  // Fetch dashboard data
  React.useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user data
        const authStatus = await authService.checkStatus();
        if (authStatus.user) {
          setUser(authStatus.user);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError(t("dashboard.failedToLoad"));
        toast.error(t("dashboard.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getViewTitle = () => {
    const currentView = getCurrentView();
    switch (currentView) {
      case "variable_library":
        return t("dashboard.tabs.variables");
      case "subscription":
        return t("dashboard.tabs.subscription");
      case "code-generator":
        return "Générateur SILAE";
      case "code-editor":
        return "Éditeur de Code";
      case "ai-generator":
        return "Générateur IA SILAE";
      default:
        return t("dashboard.tabs.profile");
    }
  };

  const renderBreadcrumb = () => {
    const breadcrumbItems: Array<{
      label: string;
      path?: string;
      isLink: boolean;
    }> = [
      {
        label: t("navigation.dashboard"),
        path: "/dashboard/profile",
        isLink: true,
      },
    ];

    // Add current view
    breadcrumbItems.push({
      label: getViewTitle(),
      isLink: false,
    });

    return (
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem>
                {item.isLink ? (
                  <BreadcrumbLink
                    onClick={() => item.path && navigate(item.path)}
                    className="cursor-pointer hover:text-foreground"
                  >
                    {item.label}
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  };

  // Check if mobile screen
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <div className="flex flex-1 overflow-hidden">
          {/* Desktop Sidebar Skeleton */}
          {!isMobile && (
            <div
              className={`${
                sidebarOpen ? "w-64" : "w-16"
              } bg-sidebar border-r border-border flex flex-col transition-all duration-300 ease-in-out`}
            >
              {/* Logo/Header */}
              <div className="px-4 border-b border-border">
                <div
                  className={`flex h-[56px] items-center ${
                    sidebarOpen ? "gap-2" : "justify-center"
                  }`}
                >
                  <Skeleton variant="circular" className="h-8 w-8" />
                  {sidebarOpen && (
                    <Skeleton variant="text" className="h-5 w-24" />
                  )}
                </div>
              </div>

              {/* Navigation Items */}
              {sidebarOpen && (
                <div className="flex-1 p-3 space-y-1">
                  <Skeleton variant="text" className="h-3 w-12 mb-3 ml-2" />
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-2 py-2 rounded-md"
                      style={{
                        animationDelay: `${i * 50}ms`,
                      }}
                    >
                      <Skeleton variant="rectangular" className="h-5 w-5" />
                      <Skeleton variant="text" className="h-4 flex-1" />
                    </div>
                  ))}
                </div>
              )}

              {/* User Profile Footer */}
              <div className="border-t border-border p-3">
                <div
                  className={`flex items-center ${sidebarOpen ? "gap-3" : "justify-center"}`}
                >
                  <Skeleton variant="circular" className="h-10 w-10" />
                  {sidebarOpen && (
                    <div className="flex-1 space-y-1.5">
                      <Skeleton variant="text" className="h-4 w-24" />
                      <Skeleton variant="text" className="h-3 w-20" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Skeleton */}
          <div className="flex-1 flex flex-col">
            {/* Header Skeleton */}
            <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex h-16 items-center px-4 md:px-6 gap-4">
                {isMobile ? (
                  <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 md:hidden"
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 p-0">
                      <div className="h-full">
                        {/* Mobile sidebar content will be rendered here */}
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="shrink-0 hidden md:flex"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                )}
                <div className="flex items-center gap-2 flex-1">
                  <Skeleton variant="text" className="h-4 w-24" />
                  <Skeleton variant="text" className="h-4 w-4" />
                  <Skeleton variant="text" className="h-4 w-32" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton variant="circular" className="h-9 w-9" />
                  <Skeleton
                    variant="rectangular"
                    className="h-9 w-24 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Content Area avec SilaeTabSkeleton */}
            <div className="flex-1 overflow-auto">
              <div className="p-6">
                <SilaeTabSkeleton />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-screen bg-background">
        <div className="flex flex-1 overflow-hidden">
          {!isMobile && (
            <DashboardSidebar
              sidebarOpen={sidebarOpen}
              user={null}
              selectedView={getCurrentView()}
              setSelectedView={(view) => navigate(`/dashboard/${view}`)}
            />
          )}

          <div className="flex-1 flex flex-col">
            <div className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex h-14 items-center px-4 md:px-6 gap-4">
                {isMobile ? (
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 md:hidden"
                      >
                        <Menu className="h-4 w-4" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 p-0">
                      <div className="h-full">
                        {/* Mobile sidebar content */}
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="shrink-0 hidden md:flex"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                )}
                {renderBreadcrumb()}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-4 md:p-6">
              <Card className="w-full max-w-md">
                <CardContent className="py-8 md:py-12">
                  <div className="text-center">
                    <div className="text-red-500 text-4xl mb-4">⚠️</div>
                    <h3 className="text-lg font-medium mb-2">
                      {t("dashboard.somethingWentWrong")}
                    </h3>
                    <p className="text-muted-foreground mb-4 text-sm md:text-base">
                      {error}
                    </p>
                    <Button onClick={() => window.location.reload()}>
                      {t("dashboard.tryAgain")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Context value
  const contextValue = {
    user,
    loading,
    error,
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {isMobile ? (
        // Mobile Layout with Sheet overlay
        <div className="flex h-screen w-full flex-col">
          {/* Mobile Header */}
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0">
                <div className="h-full">
                  <DashboardSidebar
                    sidebarOpen={true}
                    user={user}
                    selectedView={getCurrentView()}
                    setSelectedView={(view) => {
                      navigate(`/dashboard/${view}`);
                      setMobileMenuOpen(false);
                    }}
                    isMobile={true}
                  />
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex flex-1 items-center gap-2 px-3">
              {renderBreadcrumb()}
            </div>
          </header>

          {/* Mobile Main Content */}
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0 overflow-auto">
            <Outlet />
          </div>
        </div>
      ) : (
        // Desktop Layout with inline sidebar
        <div className="flex h-screen w-full">
          {/* Desktop Sidebar - Inline */}
          <div
            className={`flex flex-col border-r border-border bg-sidebar text-sidebar-foreground   ${
              sidebarOpen ? "w-64" : "w-16"
            }`}
          >
            <DashboardSidebar
              sidebarOpen={sidebarOpen}
              user={user}
              selectedView={getCurrentView()}
              setSelectedView={(view) => {
                navigate(`/dashboard/${view}`);
              }}
            />
          </div>

          {/* Desktop Main Content */}
          <div className="flex flex-1 flex-col min-w-0">
            {/* Desktop Header */}
            <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="shrink-0"
              >
                <Menu className="h-4 w-4" />
              </Button>

              <div className="flex flex-1 items-center gap-2 px-3">
                {renderBreadcrumb()}
              </div>
            </header>

            {/* Desktop Main Content Area */}
            <div className="flex flex-1 flex-col gap-4 overflow-auto">
              <Outlet />
            </div>
          </div>
        </div>
      )}
    </DashboardContext.Provider>
  );
};
